import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ScopeCreepCalculator.css';

// ── Inputs ─────────────────────────────────────────────────────────────────────
// Exact price points — user picks closest. No midpoint needed.
const RATES = [
  { label: '~$15', val: 15 },
  { label: '~$25', val: 25 },
  { label: '~$40', val: 40 },
  { label: '~$60', val: 60 },
  { label: '~$85', val: 85 },
  { label: '~$125', val: 125 },
  { label: '~$175', val: 175 },
  { label: '~$250+', val: 250 },
];

// Extra hours PER PROJECT outside the agreed scope
const EXTRA_HRS = [
  { label: '~1 hr', val: 1 },  // quick extra call, one minor revision
  { label: '~3 hrs', val: 3 },  // small feature, a couple revision rounds
  { label: '~5 hrs', val: 5 },  // moderate addition
  { label: '~10 hrs', val: 10 },  // significant scope shift
  { label: '~20 hrs', val: 20 },  // major addition (half-week extra)
  { label: '40+ hrs', val: 50 },  // feature rework, full rebrand additions, large dev scope
];

// Projects per month — covers VA/writer (high volume) to developer (low volume)
const PROJECTS_PER_MONTH = [
  { label: '1', val: 1 },
  { label: '2', val: 2 },
  { label: '3', val: 3 },
  { label: '4', val: 4 },
  { label: '5', val: 5 },
  { label: '6+', val: 8 },  // content writers, social media managers
];

// ── Witty comments (keyed to annual loss magnitude) ────────────────────────────
const LINES = {
  tiny: [   // < $3k
    "Small leak. But it's every year, without fail.",
    "You're basically giving one client a monthly discount.",
    "Not enough to complain about. Enough to fix.",
    "One project's worth of free work. Annually.",
  ],
  low: [    // $3k–$10k
    "That's a MacBook Pro. Handed over in invisible hours.",
    "You worked an extra month this year. Invoice: $0.",
    "Your clients found a very clever way to get a discount.",
    "Free hours, every project. It compounds quietly.",
    "Three hours here, five there — and suddenly it's five figures.",
  ],
  mid: [    // $10k–$30k
    "That's a down payment — built from hours you never billed.",
    "Your invoice says one thing. Your calendar says another.",
    "Most freelancers retire before they notice this number.",
    "This isn't scope creep. This is scope avalanche.",
    "You funded someone's Q4 bonus with your unbilled time.",
  ],
  high: [   // $30k+
    "At this rate, they should just put you on payroll.",
    "You're running a two-for-one deal you never agreed to.",
    "Somewhere, a client is thriving. You're just exhausted.",
    "This isn't a pricing problem — it's a documentation problem.",
    "You're the most expensive non-expense your client has.",
  ],
};

function getComment(annual, ri, hi, pi) {
  let pool;
  if (annual < 3000) pool = LINES.tiny;
  else if (annual < 10000) pool = LINES.low;
  else if (annual < 30000) pool = LINES.mid;
  else pool = LINES.high;
  return pool[(ri * 18 + hi * 6 + pi) % pool.length];
}

function getEquivalent(annual) {
  if (annual < 2000) return 'a few business dinners';
  if (annual < 4000) return 'a professional camera kit';
  if (annual < 7000) return 'a MacBook Air';
  if (annual < 11000) return 'two weeks in Japan, all in';
  if (annual < 16000) return 'a MacBook Pro M4 Max';
  if (annual < 28000) return 'three months of mortgage payments';
  if (annual < 50000) return 'a year of someone\'s salary';
  return 'a junior hire for six months';
}

function humanDays(hrs) {
  const days = hrs / 8;
  if (days < 1) return `${hrs} hour${hrs !== 1 ? 's' : ''}`;
  if (days < 5) return `${Math.round(days)} work day${Math.round(days) !== 1 ? 's' : ''}`;
  if (days < 22) return `${Math.round(days)} work days`;
  const wks = (days / 5).toFixed(1);
  return `${wks} work weeks`;
}

function fmtMoney(n) {
  if (n >= 100000) return `$${(n / 1000).toFixed(0)}k`;
  if (n >= 10000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

// ── Animated counter ───────────────────────────────────────────────────────────
function useCountUp(target, active) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  const prev = useRef(0);

  useEffect(() => {
    if (!active) { setVal(0); prev.current = 0; return; }
    const from = prev.current;
    const diff = target - from;
    const dur = Math.min(1400, 500 + Math.abs(diff) / 50);
    const t0 = Date.now();
    cancelAnimationFrame(raf.current);
    function tick() {
      const p = Math.min((Date.now() - t0) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + diff * e));
      if (p < 1) raf.current = requestAnimationFrame(tick);
      else prev.current = target;
    }
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, active]); // eslint-disable-line

  return val;
}

// ── SVG icons ──────────────────────────────────────────────────────────────────
const IconDollar = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const IconClock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconBriefcase = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <line x1="12" y1="12" x2="12" y2="12" />
  </svg>
);

// ── DayBlocks — the killer visual ─────────────────────────────────────────────
function DayBlocks({ lostDays }) {
  const MAX_SHOWN = 40;
  const shown = Math.min(lostDays, MAX_SHOWN);
  const overflow = lostDays - MAX_SHOWN;

  return (
    <div className="scc-days-wrap">
      <span className="scc-days-caption">
        {lostDays} unpaid work day{lostDays !== 1 ? 's' : ''} this year
      </span>
      <div className="scc-days-grid">
        {Array.from({ length: shown }, (_, i) => (
          <div
            key={i}
            className="scc-day-block"
            style={{ animationDelay: `${Math.min(i * 30, 600)}ms` }}
            title={`Free work day ${i + 1}`}
          />
        ))}
        {overflow > 0 && (
          <div className="scc-day-overflow">+{overflow}</div>
        )}
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function ScopeCreepCalculator() {
  const navigate = useNavigate();

  const [rateIdx, setRateIdx] = useState(null);
  const [hrsIdx, setHrsIdx] = useState(null);
  const [projIdx, setProjIdx] = useState(null);   // 0-indexed into PROJECTS_PER_MONTH

  const done = rateIdx != null && hrsIdx != null && projIdx != null;

  const rate = rateIdx != null ? RATES[rateIdx].val : 0;
  const hrsProj = hrsIdx != null ? EXTRA_HRS[hrsIdx].val : 0;
  const projMo = projIdx != null ? PROJECTS_PER_MONTH[projIdx].val : 0;

  const lostHrsPerYear = hrsProj * projMo * 12;
  const lostDays = Math.max(1, Math.round(lostHrsPerYear / 8));
  const annualLoss = lostHrsPerYear * rate;
  const monthlyLoss = hrsProj * projMo * rate;

  const displayAnnual = useCountUp(annualLoss, done);
  const comment = done ? getComment(annualLoss, rateIdx, hrsIdx, projIdx) : null;
  const equivalent = done ? getEquivalent(annualLoss) : null;

  return (
    <section className="scc-section">
      <div className="section-inner scc-wrap">

        {/* ── Left: Inputs ──────────────────────────────────────────── */}
        <div className="scc-inputs">
          <div className="scc-header">
            <p className="section-eyebrow scc-eyebrow">The real cost</p>
            <h2 className="scc-heading">
              What's scope creep<br />costing you per year?
            </h2>
          </div>

          {/* Step 1 — Rate */}
          <div className="scc-step">
            <div className="scc-step-hdr">
              <span className={`scc-step-ico ${rateIdx != null ? 'done' : ''}`}><IconDollar /></span>
              <span className="scc-step-lbl">Your hourly rate (Choose the closest)</span>
            </div>
            <div className="scc-tile-grid scc-tile-grid--rate">
              {RATES.map((r, i) => (
                <button
                  key={i}
                  className={`scc-tile ${rateIdx === i ? 'active' : ''}`}
                  onClick={() => setRateIdx(i)}
                >
                  {r.label}
                  <span className="scc-tile-unit">/hr</span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2 — Extra hours per project */}
          <div className={`scc-step ${rateIdx == null ? 'scc-locked' : ''}`}>
            <div className="scc-step-hdr">
              <span className={`scc-step-ico ${hrsIdx != null ? 'done' : ''}`}><IconClock /></span>
              <span className="scc-step-lbl">
                Per project, how many hours do clients pull you outside what was agreed?
              </span>
            </div>
            <div className="scc-tile-grid scc-tile-grid--hrs">
              {EXTRA_HRS.map((h, i) => (
                <button
                  key={i}
                  className={`scc-tile ${hrsIdx === i ? 'active' : ''}`}
                  onClick={() => rateIdx != null && setHrsIdx(i)}
                  disabled={rateIdx == null}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>

          {/* Step 3 — Projects per month */}
          <div className={`scc-step ${(rateIdx == null || hrsIdx == null) ? 'scc-locked' : ''}`}>
            <div className="scc-step-hdr">
              <span className={`scc-step-ico ${projIdx != null ? 'done' : ''}`}><IconBriefcase /></span>
              <span className="scc-step-lbl">Projects per month</span>
            </div>
            <div className="scc-proj-row">
              {PROJECTS_PER_MONTH.map((p, i) => (
                <button
                  key={i}
                  className={`scc-proj-btn ${projIdx === i ? 'active' : ''}`}
                  onClick={() => hrsIdx != null && setProjIdx(i)}
                  disabled={hrsIdx == null}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right: Result ─────────────────────────────────────────── */}
        <div className={`scc-result ${done ? 'scc-result--live' : ''}`}>
          {!done ? (
            <div className="scc-idle">
              {[
                ['Your rate', rateIdx != null],
                ['Hours per project', hrsIdx != null],
                ['Monthly volume', projIdx != null],
              ].map(([label, ok]) => (
                <div key={label} className={`scc-idle-row ${ok ? 'ok' : ''}`}>
                  <span className="scc-idle-dot" />
                  {label}
                </div>
              ))}
              <p className="scc-idle-hint">Your annual loss appears here.</p>
            </div>
          ) : (
            <>
              {/* Day blocks — the visceral visual */}
              <DayBlocks lostDays={lostDays} />

              {/* Annual loss figure */}
              <div className="scc-fig-block">
                <span className="scc-fig-pre">You're losing approximately</span>
                <div className="scc-fig">{fmtMoney(displayAnnual)}</div>
                <div className="scc-fig-sub">
                  <span className="scc-fig-mo">{fmtMoney(monthlyLoss)}/month</span>
                  <span className="scc-fig-time">{humanDays(lostHrsPerYear)} free per year</span>
                </div>
              </div>

              {/* Witty comment */}
              <p className="scc-comment">"{comment}"</p>

              {/* Tangible equivalent */}
              <p className="scc-equiv">
                That's {equivalent} — handed over in unbilled hours, every year.
              </p>

              {/* Math — show exact basis so they trust the number */}
              <p className="scc-math">
                ~{hrsProj} hrs × {projMo} proj/mo × 12 months × ${rate}/hr
              </p>

              {/* CTA */}
              <button className="scc-cta" onClick={() => navigate('/login')}>
                Start protecting your income →
              </button>
            </>
          )}
        </div>

      </div>
    </section>
  );
}
